import {
  findDeviceById,
  resolveFallbackDevice,
} from "./audio-device-selection.js";
import type { MeetingClient } from "./table-call.context.js";

interface ApplyPreferredDevicesArgs {
  meeting: MeetingClient;
  preferredMicrophoneId: string | null;
  preferredSpeakerId: string | null;
}

export interface ApplyPreferredDevicesResult {
  appliedMicrophoneId: string | null;
  appliedSpeakerId: string | null;
  availableMicrophones: MediaDeviceInfo[];
  availableSpeakers: MediaDeviceInfo[];
  preferredMicrophoneMissing: boolean;
  preferredSpeakerMissing: boolean;
}

export async function applyPreferredDevices({
  meeting,
  preferredMicrophoneId,
  preferredSpeakerId,
}: ApplyPreferredDevicesArgs): Promise<ApplyPreferredDevicesResult> {
  const [availableMicrophones, availableSpeakers] = await Promise.all([
    meeting.self.getAudioDevices(),
    meeting.self.getSpeakerDevices(),
  ]);

  const matchedSpeaker = findDeviceById(availableSpeakers, preferredSpeakerId);
  const matchedMicrophone = findDeviceById(
    availableMicrophones,
    preferredMicrophoneId
  );

  const fallbackSpeaker =
    !matchedSpeaker && preferredSpeakerId
      ? resolveFallbackDevice(availableSpeakers)
      : null;
  const fallbackMicrophone =
    !matchedMicrophone && preferredMicrophoneId
      ? resolveFallbackDevice(availableMicrophones)
      : null;

  const targetSpeaker = matchedSpeaker ?? fallbackSpeaker;
  const targetMicrophone = matchedMicrophone ?? fallbackMicrophone;

  const currentDevices = meeting.self.getCurrentDevices();
  let appliedSpeakerId = currentDevices.speaker?.deviceId ?? null;
  let appliedMicrophoneId = currentDevices.audio?.deviceId ?? null;

  if (targetSpeaker) {
    if (targetSpeaker.deviceId !== currentDevices.speaker?.deviceId) {
      await meeting.self.setDevice(targetSpeaker);
    }
    appliedSpeakerId = targetSpeaker.deviceId;
  }

  if (targetMicrophone) {
    if (targetMicrophone.deviceId !== currentDevices.audio?.deviceId) {
      await meeting.self.setDevice(targetMicrophone);
    }
    appliedMicrophoneId = targetMicrophone.deviceId;
  }

  return {
    appliedMicrophoneId,
    appliedSpeakerId,
    availableMicrophones,
    availableSpeakers,
    preferredMicrophoneMissing: Boolean(
      preferredMicrophoneId && !matchedMicrophone
    ),
    preferredSpeakerMissing: Boolean(preferredSpeakerId && !matchedSpeaker),
  };
}
